import {
  FilePenLineIcon,
  LucideFileX,
  PrinterCheckIcon,
  SendIcon,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  Checkbox,
  cn,
  V2Label,
  V2Loader,
} from "web-design-system";
import {
  getSchemeFolioHoldingsData,
  useChangeOfBrokerMutation,
  useChangeOfBrokerOptions,
  useFundFilters,
  useMfCentralStatus,
  webDownload,
} from "creso-core/hooks";
import { convertToCurrency } from "creso-core/utils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { CUSTOM_AMC_LABELS_BY_AMC_ID } from "creso-core/constants";
import type { Client, COBBrokerOption, COBOptionFolio } from "creso-core/types";
import isEmpty from "lodash/isEmpty";
import { useDownloadPdfReport } from "../../hooks";
import {
  createZipCollector,
  getPdfDownloadUrl,
  handleError,
  type ZipArchive,
} from "../../utils";
import portfolioEmptyImg from "../../assets/images/portfolio-empty.png";
import type { PdfDownloader } from "./types";
import {
  downloadFamilyCobZip,
  type FamilyCobMember,
} from "./download-family-cob-zip";

export type { FamilyCobMember };

export type ChangeOfBrokerClient = Pick<
  Client,
  "id" | "name" | "investment_account"
>;

export type ChangeOfBrokerFamily = {
  name: string;
  /** Members with an investment account; the first one's folios are previewed. */
  members: FamilyCobMember[];
};

/**
 * The change of broker body — folio selection plus next steps. Host apps wrap
 * it in their own dialog (partners adds a client chooser, clients an account
 * picker).
 */
export type ChangeOfBrokerFormProps = {
  className?: string;
  accountPicker?: React.ReactNode | null;
  /**
   * Host-app hook for importing external holdings. The CTA in the empty state
   * is only rendered when this is supplied.
   */
  onImportHoldings?: () => void;
} & (
  | {
      /** `undefined` while the host app is still resolving the client. */
      client: ChangeOfBrokerClient | undefined;
      family?: never;
      createZip?: never;
    }
  | {
      family: ChangeOfBrokerFamily;
      client?: never;
      /** Supplies the zip the member PDFs go into, keeping `jszip` in the host app. */
      createZip: () => ZipArchive;
    }
);

function IconWrapper(props: React.PropsWithChildren) {
  return <div className="p-3 bg-background rounded-xl" {...props} />;
}

type FolioItem = COBOptionFolio;

type AMCGroup = {
  amc_morningstar_code: string;
  total_value: number;
  folios: FolioItem[];
};

type BrokerGroup = {
  broker_code: string;
  broker_name: string;
  total_value: number;
  amcs: AMCGroup[];
};

function groupOptionsByAMC(options?: COBBrokerOption[]): BrokerGroup[] {
  if (!options) return [];

  return options.map((broker) => {
    const amcs: Record<string, AMCGroup> = {};

    broker.folio_details.forEach((folio) => {
      const amcCode = folio.morningstar_amc_code;
      if (!amcs[amcCode]) {
        amcs[amcCode] = {
          amc_morningstar_code: amcCode,
          total_value: 0,
          folios: [],
        };
      }
      amcs[amcCode].folios.push(folio);
      amcs[amcCode].total_value += parseFloat(folio.amount);
    });

    return {
      broker_code: broker.broker_code,
      broker_name: broker.broker_name,
      total_value: parseFloat(broker.total_amount),
      amcs: Object.values(amcs),
    };
  });
}

// Helper function to get all composite keys for a broker
const getBrokerKeys = (broker: BrokerGroup): string[] => {
  return broker.amcs.flatMap((amc) =>
    amc.folios.map(
      (folio) =>
        `${broker.broker_code}|${amc.amc_morningstar_code}|${folio.folio_id}`
    )
  );
};

// Helper function to get all composite keys for an AMC within a broker
const getAmcKeys = (brokerCode: string, amc: AMCGroup): string[] => {
  return amc.folios.map(
    (folio) => `${brokerCode}|${amc.amc_morningstar_code}|${folio.folio_id}`
  );
};

export function ChangeOfBrokerForm(props: ChangeOfBrokerFormProps) {
  const {
    family,
    client,
    createZip,
    className,
    onImportHoldings,
    accountPicker,
  } = props;
  const isFamilyMode = !!family;
  const { mutateAsync: createChangeOfBrokerRequest } =
    useChangeOfBrokerMutation();

  const investmentAccountGuid = isFamilyMode
    ? (family.members[0]?.investmentAccount ?? "")
    : (client?.investment_account ?? "");

  const { data: cobOptions, isLoading: isLoadingCobOptions } =
    useChangeOfBrokerOptions(investmentAccountGuid);

  const downloaderRef = useRef<PdfDownloader>(webDownload);
  const pdfDownloader = useCallback<PdfDownloader>(async (blob, fileName) => {
    await downloaderRef.current(blob, fileName);
  }, []);
  const [downloadReport, isGeneratingReport] =
    useDownloadPdfReport(pdfDownloader);

  const groupedData = useMemo(
    () => groupOptionsByAMC(cobOptions),
    [cobOptions]
  );

  const { data, isLoading: isLoadingFundNames } = useFundFilters(
    "provider_company_name"
  );

  const isLoading =
    isLoadingFundNames || isLoadingCobOptions || (!isFamilyMode && !client);

  // Store selected items using composite keys: broker_code-amc_code-folio_id
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());

  useEffect(() => {
    // Initialize with all items selected
    const allItems = new Set<string>();
    groupedData.forEach((broker) => {
      broker.amcs.forEach((amc) => {
        amc.folios.forEach((folio) => {
          const key = `${broker.broker_code}|${amc.amc_morningstar_code}|${folio.folio_id}`;
          allItems.add(key);
        });
      });
    });
    setSelectedItems(allItems);
  }, [groupedData]);

  // Calculate checkbox state for broker
  const getBrokerCheckboxState = (broker: BrokerGroup) => {
    const keys = getBrokerKeys(broker);
    const selectedCount = keys.filter((key) => selectedItems.has(key)).length;

    return {
      checked: selectedCount === keys.length && keys.length > 0,
      indeterminate: selectedCount > 0 && selectedCount < keys.length,
    };
  };

  // Calculate checkbox state for AMC
  const getAmcCheckboxState = (brokerCode: string, amc: AMCGroup) => {
    const keys = getAmcKeys(brokerCode, amc);
    const selectedCount = keys.filter((key) => selectedItems.has(key)).length;

    return {
      checked: selectedCount === keys.length && keys.length > 0,
      indeterminate: selectedCount > 0 && selectedCount < keys.length,
    };
  };

  // Handle broker checkbox change
  const handleBrokerChange = (broker: BrokerGroup, checked: boolean) => {
    const keys = getBrokerKeys(broker);
    setSelectedItems((prev) => {
      const newSet = new Set(prev);
      if (checked) {
        keys.forEach((key) => newSet.add(key));
      } else {
        keys.forEach((key) => newSet.delete(key));
      }
      return newSet;
    });
  };

  // Handle AMC checkbox change
  const handleAmcChange = (
    brokerCode: string,
    amc: AMCGroup,
    checked: boolean
  ) => {
    const keys = getAmcKeys(brokerCode, amc);
    setSelectedItems((prev) => {
      const newSet = new Set(prev);
      if (checked) {
        keys.forEach((key) => newSet.add(key));
      } else {
        keys.forEach((key) => newSet.delete(key));
      }
      return newSet;
    });
  };

  // Handle folio checkbox change
  const handleFolioChange = (
    brokerCode: string,
    amcCode: string,
    folioId: number,
    checked: boolean
  ) => {
    const key = `${brokerCode}|${amcCode}|${folioId}`;
    setSelectedItems((prev) => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(key);
      } else {
        newSet.delete(key);
      }
      return newSet;
    });
  };

  const [isGenerating, setIsGenerating] = useState(false);
  const [isFamilyGenerating, setIsFamilyGenerating] = useState(false);

  const generateFamilyForm = useCallback(async () => {
    if (!family || !createZip) return;

    const toastId = toast.loading("Generating change of broker forms...");
    const zip = createZip();
    // Member PDFs are collected into the zip instead of downloaded one by one.
    downloaderRef.current = createZipCollector(zip);

    try {
      setIsFamilyGenerating(true);
      await downloadFamilyCobZip({
        familyName: family.name,
        members: family.members,
        zip,
        createChangeOfBrokerRequest,
        getHoldings: (investmentAccount) =>
          getSchemeFolioHoldingsData(investmentAccount, true),
        generatePdf: async (memberClientId, fileName) => {
          await downloadReport(
            getPdfDownloadUrl(`/reports/clients/${memberClientId}/cob`),
            fileName,
            { hideToast: true }
          );
        },
        onProgress: (current, total) => {
          toast.loading(
            `Generating change of broker forms (${current}/${total})...`,
            { id: toastId }
          );
        },
      });
      toast.success("Change of broker forms downloaded successfully", {
        id: toastId,
      });
    } catch (error) {
      toast.dismiss(toastId);
      handleError(error, "Failed to generate change of broker reports");
    } finally {
      downloaderRef.current = webDownload;
      setIsFamilyGenerating(false);
    }
  }, [createChangeOfBrokerRequest, createZip, downloadReport, family]);

  const generateForm = useCallback(
    async (selectedItems: Set<string>) => {
      // Transform selectedItems into { broker_code: [folioId, ...] } format
      try {
        setIsGenerating(true);
        const brokerFolioMap: Record<string, number[]> = {};

        selectedItems.forEach((key) => {
          const parts = key.split("|");

          const folioId = parseInt(parts[parts.length - 1], 10);
          const brokerCode = parts[0];

          if (!brokerFolioMap[brokerCode]) {
            brokerFolioMap[brokerCode] = [];
          }

          if (!brokerFolioMap[brokerCode].includes(folioId)) {
            brokerFolioMap[brokerCode].push(folioId);
          }
        });

        await createChangeOfBrokerRequest({
          preference: brokerFolioMap,
          investmentAccountId: client?.investment_account ?? "",
        });

        await downloadReport(
          getPdfDownloadUrl(`/reports/clients/${client?.id}/cob`),
          `${client?.name}-change-of-broker.pdf`,
          {
            loading: "Generating change of broker form",
          }
        );
      } catch (error) {
        handleError(error, "Failed to generate change of broker report");
      } finally {
        setIsGenerating(false);
      }
    },
    [createChangeOfBrokerRequest, client, downloadReport]
  );

  const isDisabled = isGeneratingReport || isGenerating || isFamilyGenerating;

  return (
    <V2Loader
      isLoading={isLoading}
      className={cn(
        "flex flex-row overflow-hidden w-250 rounded-lg",
        className
      )}
    >
      <div className="w-180 flex flex-col gap-4 p-6 overflow-y-auto">
        <div className="flex items-center justify-between">
          <span className="text-base font-semibold">
            Select folios for change of broker
          </span>
          {accountPicker}
        </div>
        {isEmpty(groupedData) ? (
          <COBEmptyState
            investmentAccountGuid={investmentAccountGuid}
            onImportHoldings={onImportHoldings}
          />
        ) : (
          <Accordion
            className="gap-6 flex flex-col"
            type="multiple"
            defaultValue={groupedData.map((b) => b.broker_code)}
          >
            {groupedData.map((broker) => {
              return (
                <AccordionItem
                  value={broker.broker_code}
                  key={broker.broker_code}
                  className="border border-border rounded-lg overflow-hidden"
                >
                  <div className="bg-muted flex items-center gap-3 p-4">
                    <Checkbox
                      id={broker.broker_code}
                      disabled={isDisabled}
                      checked={
                        getBrokerCheckboxState(broker).indeterminate
                          ? "indeterminate"
                          : getBrokerCheckboxState(broker).checked
                      }
                      onCheckedChange={(checked) =>
                        handleBrokerChange(broker, checked as boolean)
                      }
                    />
                    <V2Label htmlFor={broker.broker_code}>
                      <div className="flex flex-col items-start gap-1">
                        <span className="font-medium text-sm">
                          {broker.broker_name}
                        </span>
                        <span className="text-sm text-muted-foreground">
                          {broker.broker_code}
                        </span>
                      </div>
                    </V2Label>
                    <span className="font-medium text-sm ml-auto mr-1">
                      {convertToCurrency(broker.total_value)}
                    </span>
                    <AccordionTrigger className="hover:no-underline ml-0" />
                  </div>
                  <AccordionContent className="pr-12 pl-4 pt-2 flex flex-col gap-2">
                    {broker.amcs.map((amc) => {
                      const amcKey = `${broker.broker_code}|${amc.amc_morningstar_code}`;
                      const amcCheckboxState = getAmcCheckboxState(
                        broker.broker_code,
                        amc
                      );
                      const msCode = amc.amc_morningstar_code;
                      const amcName =
                        CUSTOM_AMC_LABELS_BY_AMC_ID[msCode as string] ||
                        data?.[msCode as keyof typeof data] ||
                        msCode;
                      return (
                        <div
                          key={amcKey}
                          className="flex flex-col px-4 gap-2 pr-0"
                        >
                          <div className="flex items-center gap-3 py-3">
                            <Checkbox
                              id={amcKey}
                              disabled={isDisabled}
                              checked={
                                amcCheckboxState.indeterminate
                                  ? "indeterminate"
                                  : amcCheckboxState.checked
                              }
                              onCheckedChange={(checked) =>
                                handleAmcChange(
                                  broker.broker_code,
                                  amc,
                                  checked as boolean
                                )
                              }
                            />
                            <V2Label htmlFor={amcKey}>
                              <span className="text-sm font-medium">
                                {amcName}
                              </span>
                            </V2Label>
                            <span className="font-medium text-sm ml-auto">
                              {convertToCurrency(amc.total_value)}
                            </span>
                          </div>

                          {/* Folio Level */}
                          {amc.folios.map((folio) => {
                            const folioKey = `${broker.broker_code}|${amc.amc_morningstar_code}|${folio.folio_id}`;

                            return (
                              <div
                                key={folioKey}
                                className="flex items-center gap-3 pl-4 py-3 pr-0"
                              >
                                <Checkbox
                                  id={folioKey}
                                  disabled={isDisabled}
                                  checked={selectedItems.has(folioKey)}
                                  onCheckedChange={(checked) =>
                                    handleFolioChange(
                                      broker.broker_code,
                                      amc.amc_morningstar_code,
                                      folio.folio_id,
                                      checked as boolean
                                    )
                                  }
                                />
                                <V2Label htmlFor={folioKey}>
                                  <span className="text-sm">
                                    Folio no: {folio.folio_number}
                                  </span>
                                </V2Label>
                                <span className="text-sm font-medium ml-auto">
                                  {convertToCurrency(folio.amount)}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        )}
      </div>

      <div className="w-120 p-6 bg-card flex flex-col gap-6">
        <span className="text-base font-semibold">Your next steps</span>
        <div className="flex gap-3 items-center">
          <IconWrapper>
            <PrinterCheckIcon className="size-5 stroke-1.5" />
          </IconWrapper>
          <span className="text-sm font-normal">
            Verify & Print: Review folio and broker details, then take a
            printout.
          </span>
        </div>
        <div className="flex gap-3 items-center">
          <IconWrapper>
            <FilePenLineIcon className="size-5 stroke-1.5" />
          </IconWrapper>
          <span className="text-sm font-normal">
            Sign: Both client and partner to sign on each page of form.
          </span>
        </div>
        <div className="flex gap-3 items-center">
          <IconWrapper>
            <SendIcon className="size-5 stroke-1.5" />
          </IconWrapper>
          <span className="text-sm font-normal">
            Courier: Send the signed form to Creso at the office address below.
          </span>
        </div>
        <div className="p-4 flex flex-col gap-2 rounded-lg bg-background">
          <span className="text-sm font-semibold">
            Creso Technologies Private Limited
          </span>
          <span className="text-sm font-normal">
            705, Damji Shamji Business Galleria, LBS Road, Next to Toyo House,
            Kanjurmarg, Mumbai - 400078
          </span>
          <span className="text-sm">Phone: 9082930322</span>
        </div>
        <span className="px-3 text-xs text-muted-foreground">
          Once received, change of broker will be in effect in 10 to 12 business
          days.
        </span>
        <Button
          onClick={() =>
            isFamilyMode ? generateFamilyForm() : generateForm(selectedItems)
          }
          className="mt-auto"
          disabled={isDisabled}
        >
          {isDisabled ? "Generating..." : "Download CoB form"}
        </Button>
      </div>
    </V2Loader>
  );
}

function COBEmptyState(props: {
  investmentAccountGuid: string;
  onImportHoldings?: () => void;
}) {
  const { data: mfCentralStatus } = useMfCentralStatus(
    props.investmentAccountGuid
  );

  return (
    <div className="flex flex-col items-center justify-center h-full border border-border border-dashed rounded-lg">
      {mfCentralStatus?.last_fetch_at ? (
        <>
          <LucideFileX className="size-12 stroke-1 stroke-muted-foreground" />
          <span className="text-sm text-center mt-1">
            No folio available
            <br /> for change of broker
          </span>
        </>
      ) : (
        <>
          <img
            src={portfolioEmptyImg}
            className="size-20 object-cover"
            alt="Empty Portfolio"
          />
          <span className="text-sm font-semibold mt-1">
            Import client’s portfolio
          </span>
          <span className="text-sm mt-1">
            To access folios for change of broker.
          </span>
          {props.onImportHoldings ? (
            <Button
              onClick={props.onImportHoldings}
              variant="outline"
              className="mt-9"
            >
              Fetch external holdings
            </Button>
          ) : null}
        </>
      )}
    </div>
  );
}
